import React, { Fragment, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  CircularProgress,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { exportCSV, exportExcel } from "../reporting/reportingUtils";

const money = (value) => value == null
  ? "—"
  : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Number(value) || 0);
const name = (row) => row?.identity?.companyName || row?.identity?.externalId || row?.identity?.website || "Unidentified";
const idLabel = (row) => row?.identity?.externalId || row?.identity?.registrationNumber || row?.identity?.website || "—";
const decisionLabel = (value) => String(value || "Unscored").replaceAll("_", " ");
const decisionTone = (value) => ({ approve: "success", review: "warning", decline: "error", insufficient_data: "default" }[value] || "default");
const isConditionalApproval = (row) =>
  row?.assessment?.decision === "review" &&
  row?.assessment?.decisionSource === "full_underwriting" &&
  row?.assessment?.fullUnderwritingRecommendation?.decision === "approved_with_conditions";
const displayedDecision = (row) => isConditionalApproval(row) ? "approved_with_conditions" : row?.assessment?.decision;
const approvedOffer = (row) => {
  const assessment = row?.assessment || {};
  return (assessment.decision === "approve" || isConditionalApproval(row)) && assessment.recommendation?.amount != null;
};
const COMPLETED_UNDERWRITING_STATUSES = new Set(["done", "complete", "completed", "success", "succeeded", "score_ready"]);
const hasCompletedUnderwriting = (row) => row?.assessment?.decisionSource === "full_underwriting"
  || COMPLETED_UNDERWRITING_STATUSES.has(String(row?.fullUnderwriting?.status || "").toLowerCase());
const hasActiveUnderwriting = (row, refreshing, bulkUnderwritingActive = false) => refreshing.has(row?.entityId)
  || ["queued", "running", "starting", "processing", "refreshing"].includes(String(row?.fullUnderwriting?.status || "").toLowerCase())
  || (bulkUnderwritingActive && !hasCompletedUnderwriting(row));
const average = (values) => {
  const populated = values.filter((value) => value != null && Number.isFinite(Number(value))).map(Number);
  return populated.length ? populated.reduce((sum, value) => sum + value, 0) / populated.length : null;
};
const averageMonthlyVolume = (row) => row?.averageMonthlyVolume
  ?? row?.signals?.averageMonthlyVolume
  ?? row?.signals?.avgMonthlyVolume
  ?? row?.signals?.monthlyVolume
  ?? row?.signals?.averageMonthlyRevenue
  ?? null;

const featureLabel = (value) => String(value || "Signal")
  .replace(/([a-z])([A-Z])/g, "$1 $2")
  .replaceAll("_", " ")
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const arcScoreValue = (row) => {
  const value = Number(row?.arcScore?.value);
  return Number.isFinite(value) && value >= 1 && value <= 10 ? value : null;
};

export const buildArcScoreExplainability = (row = {}) => {
  const compact = row.arcScore || {};
  const compactDrivers = (compact.drivers || [])
    .filter((item) => item?.finding || item?.source)
    .slice(0, 6)
    .map((item) => ({
      source: String(item?.source || "Grounded research"),
      finding: String(item?.finding || "Supporting evidence was identified."),
      impact: String(item?.impact || "neutral"),
      severity: String(item?.severity || "low"),
    }));
  if (compact.summary || compactDrivers.length) {
    return {
      mode: "compact",
      summary: compact.summary || "CrediArc completed the grounded business-health assessment.",
      drivers: compactDrivers,
      confidence: Number.isFinite(Number(compact.confidence)) ? Number(compact.confidence) : null,
      coverage: Number.isFinite(Number(compact.evidenceCoverage?.percent)) ? Number(compact.evidenceCoverage.percent) : null,
      availableSources: (compact.evidenceCoverage?.availableSources || []).slice(0, 5),
      missingSources: (compact.evidenceCoverage?.missingSources || []).slice(0, 5),
    };
  }
  const assessment = row.assessment || {};
  const drivers = (assessment.evidence || [])
    .map((item) => ({
      feature: featureLabel(item?.feature),
      weight: Number(item?.weight || 0),
      strength: Number(item?.value),
    }))
    .filter((item) => item.weight > 0 && Number.isFinite(item.strength))
    .sort((left, right) => (right.strength * right.weight) - (left.strength * left.weight))
    .slice(0, 4);
  return {
    mode: "legacy",
    summary: null,
    drivers,
    missing: (assessment.missingFeatures || []).map(featureLabel).slice(0, 5),
    guardrails: (assessment.guardrailHits || []).map(featureLabel).slice(0, 4),
    modelVersion: assessment.modelVersion || null,
  };
};

function ArcScoreCell({ row, underwritingRunning }) {
  const score = arcScoreValue(row);
  if (underwritingRunning || score == null) return <strong>—</strong>;
  const explanation = buildArcScoreExplainability(row);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: .25 }}>
      <strong>{score.toFixed(1)}</strong>
      <Tooltip
        arrow
        enterTouchDelay={0}
        title={(
          <Box sx={{ p: .5, maxWidth: 340 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 900 }}>Why this ArcScore</Typography>
            {explanation.mode === "compact" ? (
              <>
                <Typography sx={{ fontSize: 11, mt: .4 }}>{explanation.summary}</Typography>
                {explanation.drivers.map((driver, index) => (
                  <Typography key={`${driver.source}-${index}`} sx={{ fontSize: 11, mt: .55 }}>
                    • {driver.finding} — {driver.source} ({driver.impact}, {driver.severity})
                  </Typography>
                ))}
                <Typography sx={{ fontSize: 11, mt: .65 }}>
                  {explanation.confidence == null ? "" : `${explanation.confidence}% confidence`}
                  {explanation.confidence != null && explanation.coverage != null ? " · " : ""}
                  {explanation.coverage == null ? "" : `${explanation.coverage}% source coverage`}
                </Typography>
                {!!explanation.missingSources.length && (
                  <Typography sx={{ fontSize: 11, mt: .45 }}>Missing sources: {explanation.missingSources.join(", ")}</Typography>
                )}
              </>
            ) : (
              <>
                <Typography sx={{ fontSize: 11, mt: .4 }}>
                  Weighted model signals{explanation.modelVersion ? ` · model v${explanation.modelVersion}` : ""}.
                </Typography>
                {explanation.drivers.length ? explanation.drivers.map((driver) => (
                  <Typography key={driver.feature} sx={{ fontSize: 11, mt: .45 }}>
                    • {driver.feature}: {Math.round(driver.strength * 100)}% signal strength · {driver.weight}% weight
                  </Typography>
                )) : <Typography sx={{ fontSize: 11, mt: .45 }}>No weighted driver breakdown was stored for this assessment.</Typography>}
                {!!explanation.guardrails.length && (
                  <Typography sx={{ fontSize: 11, mt: .65 }}>Policy checks: {explanation.guardrails.join(", ")}</Typography>
                )}
                {!!explanation.missing.length && (
                  <Typography sx={{ fontSize: 11, mt: .45 }}>Missing signals: {explanation.missing.join(", ")}</Typography>
                )}
              </>
            )}
          </Box>
        )}
      >
        <IconButton
          size="small"
          aria-label={`Explain ArcScore for ${name(row)}`}
          onClick={(event) => event.stopPropagation()}
          sx={{ p: .35, color: "#64748b" }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

const columns = [
  ["company", "Company"], ["applications", "Applications"], ["match", "Match"], ["score", "ArcScore"],
  ["decision", "Decision"], ["lastUnderwriting", "Last underwriting"], ["monthlyVolume", "Avg. monthly volume"],
  ["requested", "Requested"], ["recommended", "Offer"],
];
const sortLabels = { recent: "Recently added/refreshed", ...Object.fromEntries(columns) };
const timestampMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (Number.isFinite(Number(value?.seconds))) return Number(value.seconds) * 1000;
  const parsed = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
const underwritingTimestamp = (row) => Math.max(...[
  row?.fullUnderwriting?.updatedAt,
  row?.fullUnderwriting?.createdAt,
  row?.fullUnderwriting?.generatedAt,
].map(timestampMillis));
const activityTimestamp = (row) => Math.max(
  timestampMillis(row?.updatedAt),
  timestampMillis(row?.createdAt),
  underwritingTimestamp(row),
);
const formattedUnderwritingTimestamp = (row) => {
  const timestamp = underwritingTimestamp(row);
  if (!timestamp) return "Not run";
  return new Intl.DateTimeFormat("en-US", {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  }).format(new Date(timestamp));
};

const sortValue = (row, key) => ({
  company: name(row).toLowerCase(),
  applications: Number(row?.applicationCount || 0),
  match: Number(row?.match?.confidence || 0),
  score: arcScoreValue(row) ?? -1,
  decision: String(displayedDecision(row) || ""),
  lastUnderwriting: underwritingTimestamp(row),
  monthlyVolume: Number(averageMonthlyVolume(row) ?? -1),
  requested: Number(row?.requestedAmount ?? -1),
  recommended: approvedOffer(row) ? Number(row.assessment.recommendation.amount) : -1,
  recent: activityTimestamp(row),
}[key]);

const exportHeaders = [
  { key: "company", label: "Company" },
  { key: "merchantId", label: "Merchant ID" },
  { key: "registrationNumber", label: "Registration Number" },
  { key: "website", label: "Website" },
  { key: "applications", label: "Applications" },
  { key: "match", label: "Match (%)" },
  { key: "score", label: "ArcScore" },
  { key: "decision", label: "Decision" },
  { key: "risk", label: "Risk Band" },
  { key: "evidence", label: "Evidence Coverage (%)" },
  { key: "lastUnderwriting", label: "Last Underwriting" },
  { key: "averageMonthlyVolume", label: "Average Monthly Volume (USD)" },
  { key: "requested", label: "Requested Amount (USD)" },
  { key: "recommended", label: "Offer Amount (USD)" },
  { key: "term", label: "Term (Months)" },
  { key: "factor", label: "Factor Rate" },
  { key: "guardrails", label: "Policy Guardrails" },
  { key: "nextSteps", label: "Recommended Next Steps" },
];

const exportRow = (row) => ({
  company: name(row),
  merchantId: row?.identity?.externalId || "",
  registrationNumber: row?.identity?.registrationNumber || "",
  website: row?.identity?.website || "",
  applications: Number(row?.applicationCount || 0),
  match: Math.round(Number(row?.match?.confidence || 0) * 100),
  score: arcScoreValue(row) ?? "",
  decision: decisionLabel(displayedDecision(row)),
  risk: row?.assessment?.recommendation?.riskBand || "",
  evidence: Number(row?.assessment?.coverage || 0),
  lastUnderwriting: underwritingTimestamp(row) ? new Date(underwritingTimestamp(row)).toISOString() : "",
  averageMonthlyVolume: averageMonthlyVolume(row) ?? "",
  requested: row?.requestedAmount ?? "",
  recommended: approvedOffer(row) ? row.assessment.recommendation.amount : "",
  term: approvedOffer(row) ? (row.assessment.recommendation.termMonths ?? "") : "",
  factor: approvedOffer(row) ? (row.assessment.recommendation.factorRate ?? "") : "",
  guardrails: (row?.assessment?.guardrailHits || []).map((item) => String(item).replaceAll("_", " ")).join("; "),
  nextSteps: nextSteps(row).join("; "),
});

function nextSteps(row) {
  const decision = displayedDecision(row);
  if (decision === "approve" || decision === "approved_with_conditions") return ["Confirm the recommended offer and commercial terms", "Complete the stated conditions and final compliance checks", "Send the offer and monitor acceptance"];
  if (decision === "decline") return ["Review the decline drivers and guardrails", "Confirm the applicable credit-policy rule", "Record an override rationale if escalated"];
  if (decision === "insufficient_data") return ["Collect missing identity and operating evidence", "Add payment, dispute, and performance history", "Re-run the assessment when evidence is complete"];
  return ["Review weak or missing evidence", "Validate internal payment-platform signals", "Approve, resize, or decline with a recorded rationale"];
}

function RecommendationPreview({ open, row, onOpenCompany }) {
  const assessment = row?.assessment || {};
  const recommendation = assessment.recommendation || {};
  const guardrails = assessment.guardrailHits || [];
  const hasOffer = approvedOffer(row);
  return (
    <Collapse in={open} timeout="auto" unmountOnExit>
      <Box sx={{ m: 1.5, p: 2, border: "1px solid #dfe7f2", borderRadius: 2.5, bgcolor: "#f8faff" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
          <Box>
            <Typography sx={{ fontSize: 11, color: "#64748b", fontWeight: 800, textTransform: "uppercase", letterSpacing: ".05em" }}>Recommendation summary</Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: .75 }}>
              <Chip size="small" color={isConditionalApproval(row) ? "success" : decisionTone(assessment.decision)} label={decisionLabel(displayedDecision(row))} sx={{ textTransform: "capitalize", fontWeight: 800 }} />
              <Typography sx={{ color: "#17233f", fontWeight: 800 }}>
                {hasOffer ? `${money(recommendation.amount)} offer` : "No offer"}
              </Typography>
              {hasOffer && recommendation.termMonths && <Chip size="small" variant="outlined" label={`${recommendation.termMonths} months`} />}
              {hasOffer && recommendation.factorRate && <Chip size="small" variant="outlined" label={`Factor ${recommendation.factorRate}`} />}
            </Stack>
          </Box>
          <Button size="small" variant="contained" endIcon={<OpenInNewRoundedIcon />} onClick={(event) => { event.stopPropagation(); onOpenCompany(row.entityId); }}>
            Open full report
          </Button>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1.4fr" }, gap: 2, mt: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>Decision drivers</Typography>
            <Typography sx={{ mt: .5, color: "#334155", fontSize: 13.5 }}>
              Score {assessment.score ?? "—"} · {assessment.coverage || 0}% evidence · {recommendation.riskBand || "unscored"} risk
            </Typography>
            <Stack direction="row" spacing={.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              {(guardrails.length ? guardrails : ["No policy guardrails triggered"]).map((item) => (
                <Chip key={item} size="small" variant="outlined" color={guardrails.length ? "warning" : "success"} label={String(item).replaceAll("_", " ")} sx={{ textTransform: "capitalize" }} />
              ))}
            </Stack>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>Next steps</Typography>
            <Stack spacing={.55} sx={{ mt: .5 }}>
              {nextSteps(row).map((step, index) => <Typography key={step} sx={{ color: "#334155", fontSize: 13.5 }}><strong>{index + 1}.</strong> {step}</Typography>)}
            </Stack>
          </Box>
        </Box>
      </Box>
    </Collapse>
  );
}

export default function PaymentPlatformExecutiveTable({
  rows = [],
  onOpenCompany,
  onRefreshCompany,
  onDeleteCompany,
  refreshingEntityIds = [],
  deletingEntityIds = [],
  onExportReport,
  exportingReportEntityIds = [],
  onExportAll,
  onSortChange,
  serverSorted = false,
  filteredTotal,
  hideExport = false,
  bulkUnderwritingActive = false,
  selectedEntityIds = [],
  onSelectionChange,
  viewport = "portfolio-assessments",
  emptyText = "No companies match the current search and filters.",
}) {
  const [sort, setSort] = useState({ key: "recent", direction: "desc" });
  const [expandedId, setExpandedId] = useState(null);
  const [exportAnchor, setExportAnchor] = useState(null);
  const [exportingAll, setExportingAll] = useState("");
  const [exportError, setExportError] = useState("");
  const [actionAnchor, setActionAnchor] = useState(null);
  const [actionRow, setActionRow] = useState(null);
  const refreshing = useMemo(() => new Set(refreshingEntityIds), [refreshingEntityIds]);
  const deleting = useMemo(() => new Set(deletingEntityIds), [deletingEntityIds]);
  const exportingReports = useMemo(() => new Set(exportingReportEntityIds), [exportingReportEntityIds]);
  const selected = useMemo(() => new Set(selectedEntityIds), [selectedEntityIds]);
  const selectionEnabled = typeof onSelectionChange === "function";
  const showReportExport = typeof onExportReport === "function";
  const showActionMenu = typeof onRefreshCompany === "function" || typeof onDeleteCompany === "function" || showReportExport;
  const showActions = showActionMenu;
  const closeActions = () => {
    setActionAnchor(null);
    setActionRow(null);
  };
  const sortedRows = useMemo(() => serverSorted ? rows : [...rows].sort((left, right) => {
    const a = sortValue(left, sort.key);
    const b = sortValue(right, sort.key);
    const result = typeof a === "string" ? a.localeCompare(b) : a - b;
    return sort.direction === "asc" ? result : -result;
  }), [rows, sort, serverSorted]);
  const activeUnderwritingCount = useMemo(
    () => sortedRows.filter((row) => hasActiveUnderwriting(row, refreshing, bulkUnderwritingActive)).length,
    [sortedRows, refreshing, bulkUnderwritingActive],
  );
  const visibleEntityIds = useMemo(
    () => sortedRows.map((row) => row.entityId).filter(Boolean),
    [sortedRows],
  );
  const selectedVisibleCount = visibleEntityIds.filter((entityId) => selected.has(entityId)).length;
  const allVisibleSelected = visibleEntityIds.length > 0 && selectedVisibleCount === visibleEntityIds.length;
  const toggleAllVisible = (checked) => {
    const next = new Set(selectedEntityIds);
    visibleEntityIds.forEach((entityId) => {
      if (checked) next.add(entityId);
      else next.delete(entityId);
    });
    onSelectionChange?.([...next]);
  };
  const toggleSelected = (entityId, checked) => {
    const next = new Set(selectedEntityIds);
    if (checked) next.add(entityId);
    else next.delete(entityId);
    onSelectionChange?.([...next]);
  };
  const changeSort = (key) => setSort((current) => {
    const next = { key, direction: current.key === key && current.direction === "asc" ? "desc" : "asc" };
    onSortChange?.(next);
    return next;
  });
  const exportRows = useMemo(() => sortedRows.map(exportRow), [sortedRows]);
  const summary = useMemo(() => {
    const completedRows = sortedRows.filter((row) => !hasActiveUnderwriting(row, refreshing, bulkUnderwritingActive));
    const decisionCounts = completedRows.reduce((counts, row) => {
      const decision = displayedDecision(row) || "unscored";
      counts[decision] = (counts[decision] || 0) + 1;
      return counts;
    }, {});
    return {
      rows: sortedRows.length,
      applications: sortedRows.reduce((sum, row) => sum + Number(row?.applicationCount || 0), 0),
      match: average(sortedRows.map((row) => Number(row?.match?.confidence) * 100)),
      score: average(completedRows.map(arcScoreValue)),
      approved: Number(decisionCounts.approve || 0) + Number(decisionCounts.approved_with_conditions || 0),
      underwritten: completedRows.filter((row) => underwritingTimestamp(row) > 0).length,
      monthlyVolume: average(sortedRows.map(averageMonthlyVolume)),
      requested: sortedRows.reduce((sum, row) => sum + Number(row?.requestedAmount || 0), 0),
      recommended: completedRows.reduce((sum, row) => sum + (approvedOffer(row) ? Number(row.assessment.recommendation.amount || 0) : 0), 0),
    };
  }, [sortedRows, refreshing, bulkUnderwritingActive]);
  const exportFilename = `crediarc-${viewport}-${new Date().toISOString().slice(0, 10)}`;
  const runPageExport = (format) => {
    setExportAnchor(null);
    if (format === "csv") exportCSV(exportRows, exportHeaders, exportFilename);
    else exportExcel(exportRows, exportHeaders, exportFilename, "MCA Assessments");
  };
  const runFullExport = async (format) => {
    setExportAnchor(null);
    setExportError("");
    setExportingAll(format);
    try {
      await onExportAll?.({ format, sortKey: sort.key, sortDirection: sort.direction });
    } catch (error) {
      setExportError(error?.response?.data?.message || error?.message || "Could not export the filtered portfolio.");
    } finally {
      setExportingAll("");
    }
  };

  return (
    <>
      <Box sx={{ px: 2.75, py: 1, bgcolor: "#f8faff", borderTop: "1px solid #edf0f5", borderBottom: "1px solid #edf0f5", display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <Chip size="small" color="primary" variant="outlined" label="Executive view" sx={{ fontWeight: 800 }} />
        <Typography sx={{ color: "#64748b", fontSize: 12.5 }}>Click a company name to open its full report. Use the chevron to preview its recommendation.</Typography>
        <Typography sx={{ color: "#8a96a8", fontSize: 12, ml: { md: "auto" } }}>{serverSorted ? "Full portfolio" : "Current page"} sorted by {sortLabels[sort.key]} {sort.direction === "asc" ? "↑" : "↓"}</Typography>
        {!hideExport && (
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownloadOutlinedIcon />}
              disabled={!sortedRows.length || Boolean(exportingAll)}
              onClick={(event) => setExportAnchor(event.currentTarget)}
              sx={{ ml: .5, borderColor: "#d4deec", color: "#334155", borderRadius: "8px", textTransform: "none", fontWeight: 750 }}
            >
              {exportingAll ? "Preparing export…" : "Export"}
            </Button>
            <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
              {onExportAll && (
                <ListSubheader sx={{ lineHeight: "30px", fontSize: 11, fontWeight: 800, textTransform: "uppercase", color: "#64748b" }}>
                  Complete filtered portfolio · {Number(filteredTotal ?? sortedRows.length).toLocaleString()} rows
                </ListSubheader>
              )}
              {onExportAll && <MenuItem onClick={() => runFullExport("xlsx")}>Excel workbook — all filtered rows</MenuItem>}
              {onExportAll && <MenuItem onClick={() => runFullExport("csv")}>CSV data — all filtered rows</MenuItem>}
              <ListSubheader sx={{ lineHeight: "30px", fontSize: 11, fontWeight: 800, textTransform: "uppercase", color: "#64748b" }}>
                Current page · {sortedRows.length} rows
              </ListSubheader>
              <MenuItem onClick={() => runPageExport("xlsx")}>Excel workbook — current page</MenuItem>
              <MenuItem onClick={() => runPageExport("csv")}>CSV data — current page</MenuItem>
            </Menu>
          </>
        )}
      </Box>
      {activeUnderwritingCount > 0 && (
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          {activeUnderwritingCount.toLocaleString()} {activeUnderwritingCount === 1 ? "company is" : "companies are"} still being underwritten. Previous scores, decisions, risk, and offers stay hidden until each new report finishes.
        </Alert>
      )}
      {exportError && <Alert severity="error" onClose={() => setExportError("")} sx={{ borderRadius: 0 }}>{exportError}</Alert>}
      <TableContainer data-mca-table-viewport={viewport} sx={{ maxHeight: { xs: 340, md: 390 }, overflow: "auto", scrollbarGutter: "stable" }}>
        <Table stickyHeader size="small" sx={{ minWidth: showActions ? 1450 : 1220 }}>
          <TableHead><TableRow>{columns.map(([key, label]) => (
            <TableCell key={key} sortDirection={sort.key === key ? sort.direction : false} sx={{ py: 1.25, color: "#64748b", bgcolor: "#f7f9fc", borderColor: "#e9edf4", fontSize: 11, letterSpacing: ".035em", textTransform: "uppercase", fontWeight: 800 }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {selectionEnabled && key === "company" && (
                  <Checkbox
                    size="small"
                    inputProps={{ "aria-label": "Select all companies on this page" }}
                    checked={allVisibleSelected}
                    indeterminate={selectedVisibleCount > 0 && !allVisibleSelected}
                    onChange={(event) => toggleAllVisible(event.target.checked)}
                    onClick={(event) => event.stopPropagation()}
                    sx={{ p: .5, mr: .5 }}
                  />
                )}
                <TableSortLabel active={sort.key === key} direction={sort.key === key ? sort.direction : "asc"} onClick={() => changeSort(key)}>{label}</TableSortLabel>
              </Box>
            </TableCell>
          ))}
            {showActions && (
              <TableCell sx={{ py: 1.25, color: "#64748b", bgcolor: "#f7f9fc", borderColor: "#e9edf4", fontSize: 11, letterSpacing: ".035em", textTransform: "uppercase", fontWeight: 800 }}>
                Actions
              </TableCell>
            )}
          </TableRow></TableHead>
          <TableBody>
            {sortedRows.map((row) => {
              const expanded = expandedId === row.entityId;
              const underwritingRunning = hasActiveUnderwriting(row, refreshing, bulkUnderwritingActive);
              return <Fragment key={row.entityId}>
                <TableRow hover aria-expanded={expanded && !underwritingRunning} sx={{ cursor: underwritingRunning ? "default" : "pointer", "& td": { py: 1.2, borderColor: expanded ? "transparent" : "#edf0f5" }, "&:hover": { bgcolor: "#f8faff !important" } }} onClick={() => !underwritingRunning && setExpandedId(expanded ? null : row.entityId)}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {selectionEnabled && (
                        <Checkbox
                          size="small"
                          inputProps={{ "aria-label": `Select ${name(row)}` }}
                          checked={selected.has(row.entityId)}
                          disabled={deleting.has(row.entityId)}
                          onChange={(event) => toggleSelected(row.entityId, event.target.checked)}
                          onClick={(event) => event.stopPropagation()}
                          sx={{ p: .5 }}
                        />
                      )}
                      <Box>{underwritingRunning ? <CircularProgress size={19} /> : expanded ? <KeyboardArrowUpRoundedIcon color="primary" /> : <KeyboardArrowDownRoundedIcon sx={{ color: "#718096" }} />}</Box>
                      <Box>
                        <Button
                          variant="text"
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            onOpenCompany(row.entityId);
                          }}
                          sx={{ display: "block", minWidth: 0, p: 0, color: "#2469d8", fontWeight: 800, fontSize: 13.5, lineHeight: 1.35, textTransform: "none", textAlign: "left", justifyContent: "flex-start" }}
                        >
                          {name(row)}
                        </Button>
                        <Typography variant="caption" color="text.secondary">{idLabel(row)}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{Number(row.applicationCount || 0).toLocaleString()}</TableCell>
                  <TableCell><Chip size="small" variant="outlined" label={`${Math.round(Number(row?.match?.confidence || 0) * 100)}%`} /></TableCell>
                  <TableCell><ArcScoreCell row={row} underwritingRunning={underwritingRunning} /></TableCell>
                  <TableCell>{underwritingRunning
                    ? <Chip size="small" color="info" label="Underwriting" sx={{ fontWeight: 800 }} />
                    : <Chip size="small" color={isConditionalApproval(row) ? "success" : decisionTone(row?.assessment?.decision)} label={decisionLabel(displayedDecision(row))} sx={{ textTransform: "capitalize", fontWeight: 800 }} />}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{underwritingRunning ? "In progress" : formattedUnderwritingTimestamp(row)}</TableCell>
                  <TableCell>{averageMonthlyVolume(row) == null ? "Not supplied" : money(averageMonthlyVolume(row))}</TableCell>
                  <TableCell>{row.requestedAmount == null ? "Not supplied" : money(row.requestedAmount)}</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>
                    {underwritingRunning ? "—" : approvedOffer(row)
                      ? money(row.assessment.recommendation.amount)
                      : row?.assessment?.decisionSource === "full_underwriting" ? "No offer" : "—"}
                  </TableCell>
                  {showActions && (
                    <TableCell>
                      <Tooltip title="Company actions">
                        <span>
                          <IconButton
                            size="small"
                            aria-label={underwritingRunning
                              ? `Refreshing underwriting for ${name(row)}`
                              : exportingReports.has(row.entityId)
                                ? `Exporting report for ${name(row)}`
                                : `Actions for ${name(row)}`}
                            disabled={deleting.has(row.entityId) || underwritingRunning || exportingReports.has(row.entityId)}
                            onClick={(event) => {
                              event.stopPropagation();
                              setActionAnchor(event.currentTarget);
                              setActionRow(row);
                            }}
                          >
                            {deleting.has(row.entityId) || exportingReports.has(row.entityId) ? (
                              <CircularProgress size={18} />
                            ) : underwritingRunning ? (
                              <RefreshRoundedIcon
                                data-testid={`underwriting-refresh-${row.entityId}`}
                                sx={{
                                  animation: "underwriting-refresh-spin .8s linear infinite",
                                  "@keyframes underwriting-refresh-spin": {
                                    from: { transform: "rotate(0deg)" },
                                    to: { transform: "rotate(360deg)" },
                                  },
                                }}
                              />
                            ) : (
                              <MoreVertRoundedIcon />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
                <TableRow><TableCell colSpan={showActions ? 10 : 9} sx={{ p: 0, borderColor: expanded && !underwritingRunning ? "#dfe7f2" : "transparent", bgcolor: "#fff" }}><RecommendationPreview open={expanded && !underwritingRunning} row={row} onOpenCompany={onOpenCompany} /></TableCell></TableRow>
              </Fragment>;
            })}
            {!sortedRows.length && <TableRow><TableCell colSpan={showActions ? 10 : 9} align="center" sx={{ py: 6, color: "text.secondary" }}>{emptyText}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
      {Boolean(sortedRows.length) && (
        <Box
          data-mca-table-summary
          sx={{
            overflowX: "auto",
            bgcolor: "#eef4ff",
            borderTop: "2px solid #cbdcf8",
            borderBottom: "1px solid #dce7f8",
          }}
        >
          <Box
            sx={{
              minWidth: showActions ? 1450 : 1220,
              px: 2,
              py: 1.4,
              display: "grid",
              gridTemplateColumns: showActions
                ? "2.45fr .95fr .85fr .8fr 1fr 1.25fr 1.25fr 1fr 1.1fr 230px"
                : "2.45fr .95fr .85fr .8fr 1fr 1.25fr 1.25fr 1fr 1.1fr",
              alignItems: "center",
              columnGap: 2,
              color: "#17233f",
              "& > *": { minWidth: 0, fontSize: 12, fontWeight: 800, whiteSpace: "nowrap" },
            }}
          >
            <Typography sx={{ fontWeight: "900 !important" }}>Filtered page · {summary.rows} merchants</Typography>
            <Typography>{summary.applications.toLocaleString()} applications</Typography>
            <Typography>{summary.match == null ? "—" : `${summary.match.toFixed(1)}% avg`}</Typography>
            <Typography>{summary.score == null ? "—" : `${summary.score.toFixed(1)} avg`}</Typography>
            <Typography>{summary.approved.toLocaleString()} approved</Typography>
            <Typography>{summary.underwritten.toLocaleString()} underwritten</Typography>
            <Typography>{summary.monthlyVolume == null ? "—" : `${money(summary.monthlyVolume)} avg`}</Typography>
            <Typography>{money(summary.requested)}</Typography>
            <Typography>{money(summary.recommended)}</Typography>
            {showActions && <Box />}
          </Box>
        </Box>
      )}
      <Menu
        anchorEl={actionAnchor}
        open={Boolean(actionAnchor)}
        onClose={closeActions}
        onClick={(event) => event.stopPropagation()}
      >
        {showReportExport && (
          <MenuItem
            disabled={!actionRow || exportingReports.has(actionRow?.entityId)}
            onClick={() => {
              const entityId = actionRow?.entityId;
              closeActions();
              if (entityId) onExportReport(entityId);
            }}
          >
            <FileDownloadOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
            {exportingReports.has(actionRow?.entityId) ? "Exporting…" : "Export report"}
          </MenuItem>
        )}
        {typeof onRefreshCompany === "function" && (
          <MenuItem
            disabled={!actionRow || hasActiveUnderwriting(actionRow, refreshing)}
            onClick={() => {
              const entityId = actionRow?.entityId;
              closeActions();
              if (entityId) onRefreshCompany(entityId);
            }}
          >
            <RefreshRoundedIcon fontSize="small" sx={{ mr: 1 }} />
            {hasActiveUnderwriting(actionRow, refreshing) ? "Refreshing…" : "Refresh"}
          </MenuItem>
        )}
        {typeof onDeleteCompany === "function" && (
          <MenuItem
            disabled={!actionRow || hasActiveUnderwriting(actionRow, refreshing) || deleting.has(actionRow?.entityId)}
            onClick={() => {
              const row = actionRow;
              closeActions();
              if (row) onDeleteCompany(row);
            }}
            sx={{ color: "error.main" }}
          >
            <DeleteOutlineRoundedIcon fontSize="small" sx={{ mr: 1 }} />
            Delete company
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
